import { useMemo } from 'react'
import Apptimize from '@apptimize/apptimize-web-sdk'
import { useAtomValue } from 'jotai'
import MarketingMFE from 'marketing-mfe'
import { useLocation } from 'react-router-dom'

import routes from 'const/routes'
import { useCreditAccounts } from 'const/syfApiQueries'
import getMfeConfig from 'helpers/getMFEConfig'
import getAccountDisplayName from 'helpers/getAccountDisplayName'
import { modalStatusAtom, oneTrustConsentAtom } from 'store/atoms'
import { selectedAccountAtom } from 'store/atoms/selectedAccount.atom'
import { analyticsConfig } from '@syf-utils/analytics'
import { getBrandRefreshPhase } from 'helpers/getBrandRefreshPhase'
import isFeatureFlagEnabled from 'helpers/isFeatureFlagEnabled'
import { jotaiStore } from 'store'
import { getAnalyticsV2Config } from './getAnalyticsV2Config'

const MARKETING_CAMPAIGN_DEFAULTS = ['VistaAcctSummary', 'VistaAcctServicing']

const getCampaignIds = () => {
  const [summaryId, servicingId] = Apptimize.getStringArray(
    'marketingOffersCampaignId',
    MARKETING_CAMPAIGN_DEFAULTS
  )
  return { summaryId, servicingId }
}

const Marketing = () => {
  const { pathname } = useLocation()
  const selectedAccount = useAtomValue(selectedAccountAtom)
  const hasTargetingConsent = useAtomValue(oneTrustConsentAtom)
  const {
    data: { accounts = [] } = {},
    isLoading: isAccountsLoading,
    isError: isCreditAccountsAPIError
  } = useCreditAccounts()

  const isAccountServicesPage = pathname === routes.ACCOUNT_SERVICES

  const showMarketingOffers =
    !isAccountsLoading && !isCreditAccountsAPIError && hasTargetingConsent

  const analyticsConfigV2 = useMemo(
    () =>
      getAnalyticsV2Config(isAccountServicesPage, accounts, selectedAccount),
    [isAccountServicesPage, accounts, selectedAccount]
  )

  const brandRefreshPhase = getBrandRefreshPhase()

  const shouldEarlyReturn =
    !showMarketingOffers ||
    accounts.length === 0 ||
    (isAccountServicesPage && !selectedAccount)

  if (shouldEarlyReturn) return null

  const { summaryId, servicingId } = getCampaignIds()
  const campaignId = isAccountServicesPage ? servicingId : summaryId

  const brands = getAccountDisplayName(
    accounts,
    isAccountServicesPage ? selectedAccount : null
  )
  const isWideSingleOffer = isAccountServicesPage || accounts.length >= 2
  const mfeConfig = getMfeConfig('mfe')

  return (
    <MarketingMFE
      mfeConfig={mfeConfig}
      brands={brands}
      isWideSingleOffer={isWideSingleOffer}
      campaignId={campaignId}
      analyticsConfig={analyticsConfig}
      analyticsConfigV2={analyticsConfigV2}
      data-test="marketing-mfe"
      alignment={isAccountServicesPage ? 'center' : 'left'}
      featureFlags={{
        brandRefreshPhase,
        isCustomCardArtEnabled: true,
        preApprovedOffersEnabled: isFeatureFlagEnabled(
          'isPreApprovedOffersEnabled'
        )
      }}
      store={jotaiStore}
      atoms={{ selectedAccountAtom, modalStatusAtom }}
    />
  )
}

export default Marketing
